<template>
  <base-common-content
    data-type="base-agent-list"
    :options="searchResults"
    :data-key="DATA_KEY"
    :use-search="useSearch"
    :size="size"
    :show-options="showOptions"
    :is-searching="isSearching"
    :is-loading="isLoading"
  >
    <template #search>
      <div
        v-if="useSearch"
        class="search"
      >
        <base-search-input
          v-if="navigation === 'results'"
          :search="searchOptions.search"
          :size="size"
          :is-disabled="isLoading || isDisabled"
          @search-change="onSearchChange"
        />

        <or-button
          v-if="useSearchOptions"
          :data-button="searchOptionsRole"
          :disabled="isLoading"
          @click="onToggleSearchOptions"
        >
          {{ searchOptionsLabel }}
          <or-icon :icon="searchOptionsIcon" />
        </or-button>
      </div>
    </template>

    <!-- Display either search options or "No Agents" warning instead of options -->
    <template #default>
      <base-agent-search-options
        v-if="navigation === 'options'"
        :options="searchOptions"
        :statuses="statuses"
        :rule-groups="ruleGroups"
        :is-disabled="isLoading || isDisabled || !canChangeSearchOptions"
        @options-change="onSearchOptionsChange"
      />

      <div
        v-if="navigation === 'results' && noAgentsWarning"
        class="no-agents-warning"
      >
        {{ noAgentsWarning }}
      </div>
    </template>

    <!-- We can only append to the list of options -->
    <template
      v-if="navigation === 'results'"
      #append
    >
      <slot name="options-append" />
    </template>

    <template #option="{ option }">
      <base-item
        :avatar="avatar"
        :person="option"
        :title="getName(option)"
        :description="option.email"
        :status="getStatusLabel(option)"
        :status-type="getStatusType(option)"
        :size="size"
        :actions="actions"
        :actions-type="actionsType"
        :is-clickable="useDefaultAction"
        :is-disabled="isDisabled"
        @action-click="onActionClick({ option, key: $event })"
        @item-click="$emit('item-click', option)"
      />
    </template>
  </base-common-content>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue-demi';
import {
  OrButtonV3 as OrButton,
  OrIconV3 as OrIcon,
} from '@onereach/ui-components-25';

import BaseCommonContent from './BaseCommonContent.vue';
import BaseSearchInput from './BaseSearchInput.vue';
import BaseItem from './BaseItem.vue';
import BaseAgentSearchOptions from './BaseAgentSearchOptions.vue';
import { IAgent, AgentStatus } from '@/types';
import { AgentSearchOptions } from '../../types';
import { RuleChecker, getAgentName, getStatusLabel } from '@/common';
import { AGENT_STATUS_API } from '@/constants';
import {
  AVATAR_PROP,
  AGENTS_PROP,
  SIZE_PROP,
  ITEM_ACTIONS_PROP,
  ITEM_ACTIONS_DISPLAY_PROP,
  AGENT_SEARCH_OPTIONS_PROP,
  STATUS_OPTIONS_PROP,
  RULE_GROUPS_PROP,
} from '../../constants';

export default defineComponent({
  name: 'BaseAgentList',
  components: {
    BaseCommonContent,
    BaseSearchInput,
    BaseItem,
    BaseAgentSearchOptions,
    OrButton,
    OrIcon,
  },
  props: {
    avatar: AVATAR_PROP,
    agents: AGENTS_PROP,
    statuses: STATUS_OPTIONS_PROP,
    ruleGroups: RULE_GROUPS_PROP,
    actions: ITEM_ACTIONS_PROP,
    actionsType: ITEM_ACTIONS_DISPLAY_PROP,
    defaultSearchOptions: AGENT_SEARCH_OPTIONS_PROP,
    size: SIZE_PROP,
    useSearch: {
      type: Boolean,
      default: true,
    },
    useSearchOptions: {
      type: Boolean,
      default: true,
    },
    canChangeSearchOptions: {
      type: Boolean,
      default: true,
    },
    useDefaultAction: Boolean,
    isLoading: Boolean,
    isDisabled: Boolean,
  },
  emits: ['search-change', 'item-click', 'action-click'],
  data() {
    return {
      navigation: 'results' as 'results' | 'options',
      searchOptions: { search: '', ...this.defaultSearchOptions } as AgentSearchOptions,
      searchResults: this.agents,
      searchTimeout: undefined as number | undefined,
      isSearching: false,
      SEARCH_DELAY_TIMEOUT: 500,
      DATA_KEY: 'agentId',
    };
  },
  computed: {
    searchOptionsLabel() {
      switch (this.navigation) {
        case 'options':
          return this.$t('hitl.modules.agent-picker.search-results');
        case 'results':
        default:
          return this.$t('hitl.modules.agent-picker.search-options');
      }
    },

    searchOptionsIcon() {
      switch (this.navigation) {
        case 'options':
          return 'person_search';
        case 'results':
        default:
          return 'manage_search';
      }
    },

    searchOptionsRole() {
      switch (this.navigation) {
        case 'options':
          return 'show-results';
        case 'results':
        default:
          return 'show-options';
      }
    },

    ruleGroupsByTag() {
      const result: Record<string, string[]> = {};

      for (const group of this.ruleGroups) {
        for (const rule of group.rules) {
          result[rule]
            ? result[rule].push(group.groupId)
            : result[rule] = [group.groupId];
        }
      }

      return result;
    },

    noAgentsWarning() {
      if (this.searchResults.length || this.$slots['options-append']) {
        return '';
      }

      const isSearchEmpty = Object.values(this.searchOptions).every(v => _.isEmpty(v));
      return isSearchEmpty
        ? this.$t('hitl.modules.agent-picker.no-agents-available')
        : this.$t('hitl.modules.agent-picker.no-agents-search-options');
    },

    showOptions() {
      return this.navigation === 'results' && !this.noAgentsWarning;
    },
  },
  watch: {
    agents() {
      this.applyActiveSearchTimeout();
    },
    defaultSearchOptions() {
      this.searchOptions = { search: '', ...this.defaultSearchOptions } as AgentSearchOptions;
      this.applyActiveSearchTimeout();
    },
  },
  created() {
    this.applyActiveSearchTimeout();
  },
  methods: {
    getName(agent: IAgent) {
      return getAgentName(agent, this.$t.bind(this));
    },

    getStatus(agent: IAgent) {
      return agent.statusData?.status || AGENT_STATUS_API.OFFLINE;
    },

    getStatusLabel(agent: IAgent) {
      const data = agent.statusData as AgentStatus;
      return getStatusLabel({ data, $t: this.$t.bind(this) });
    },

    getStatusType(agent: IAgent) {
      const status = this.getStatus(agent);
      switch (status) {
        case AGENT_STATUS_API.AVAILABLE:
          return 'success';
        case AGENT_STATUS_API.BUSY:
          return 'warning';
        default:
          return 'neutral';
      }
    },

    onActionClick(args: { option: IAgent; key: string; }) {
      const { option: agent, key } = args;
      this.$emit('action-click', { agent, key });
    },

    onSearchChange(search: string) {
      this.$emit('search-change', search);
      this.searchOptions = { ...this.searchOptions, search };
      this.applyActiveSearchTimeout();
    },

    /** Introduce a delay between search calls since they can block UI */
    applyActiveSearchTimeout() {
      clearTimeout(this.searchTimeout);
      this.isSearching = true;

      this.searchTimeout = window.setTimeout(() => {
        this.applyActiveSearch();
        this.isSearching = false;
      }, this.SEARCH_DELAY_TIMEOUT);
    },

    /** TODO: Improve agent status key search */
    applyActiveSearch() {
      const SEARCH_KEYS = ['email', 'username'];
      const searchResults: IAgent[] = [];

      for (const agent of this.agents) {
        if (this.searchOptions.status?.length) {
          const statusTerm = (agent.statusData as AgentStatus).key
            || agent.statusData.status?.toLowerCase();

          if (!this.searchOptions.status.includes(statusTerm)) {
            continue;
          }
        }

        if (this.searchOptions.ruleGroupIds?.length) {
          if (!_.intersection(agent.ruleGroupIds, this.searchOptions.ruleGroupIds).length) {
            continue;
          }
        }

        if (this.searchOptions.ruleTags?.length) {
          const check = RuleChecker.checkAgentRules({
            agent,
            tags: this.searchOptions.ruleTags,
            ruleGroups: this.ruleGroups,
          });

          if (!check) {
            continue;
          }
        }

        const searchTerm = (this.searchOptions.search || '').trim().toLowerCase();
        const name = [
          _.get(agent, 'data.firstName', ''),
          _.get(agent, 'data.lastName', ''),
        ].join(' ').trim().toLowerCase();

        if (name.includes(searchTerm)) {
          searchResults.push(agent);
          continue;
        }

        for (const key of SEARCH_KEYS) {
          const value = _.get(agent, key, '').trim().toLowerCase();
          if (value.includes(searchTerm)) {
            searchResults.push(agent);
            continue;
          }
        }
      }

      this.searchResults = searchResults;
    },

    onToggleSearchOptions() {
      if (this.navigation === 'results') {
        this.navigation = 'options';
        return;
      }

      this.navigation = 'results';
      this.applyActiveSearchTimeout();
    },

    onSearchOptionsChange(options: AgentSearchOptions) {
      this.searchOptions = options;
      this.$emit('search-change', this.searchOptions.search);
    },
  },
});
</script>

<style lang="scss" scoped>
[data-type='base-agent-list'] {
  --search-bar-item-height: 2.25rem;

  &.is-mobile {
    --search-bar-item-height: 2.5rem;

    .search {
      flex-direction: column;
    }

    [data-component='base-search-input'] {
      flex: 0 0 auto;
    }
  }

  .search {
    display: flex;
    gap: var(--s-4);
    width: 100%;
  }

  .no-agents-warning {
    display: flex;
    flex: 1 1 100%;
    align-items: center;
    justify-content: center;
    margin: var(--s-5);
    color: var(--or-c-outline);
  }

  [data-button] {
    height: var(--search-bar-item-height);
    padding: 0 var(--s-4);
  }

  [data-button='show-results'] {
    width: 100%;
  }

  [data-component='base-agent-search-options'] {
    margin: 0 var(--s-5);
  }
}
</style>
